// Sidebar structure and breadcrumb labels.
//
// Extracted from components/layout.tsx so the command palette can offer the
// same destinations without importing the shell (which imports the palette).
// One list, so a new page can never appear in the sidebar and be missing
// from search.

import type { NavSection } from "@veil/ui/layouts";
import Activity from "lucide-react/dist/esm/icons/activity";
import Boxes from "lucide-react/dist/esm/icons/boxes";
import CircleArrowUp from "lucide-react/dist/esm/icons/circle-arrow-up";
import GitBranch from "lucide-react/dist/esm/icons/git-branch";
import Globe from "lucide-react/dist/esm/icons/globe";
import LayoutDashboard from "lucide-react/dist/esm/icons/layout-dashboard";
import MonitorPlay from "lucide-react/dist/esm/icons/monitor-play";
import Network from "lucide-react/dist/esm/icons/network";
import Package from "lucide-react/dist/esm/icons/package";
import Server from "lucide-react/dist/esm/icons/server";
import SettingsIcon from "lucide-react/dist/esm/icons/settings";
import Upload from "lucide-react/dist/esm/icons/upload";

export const NAV_SECTIONS: NavSection[] = [
	{
		label: "Overview",
		items: [
			{ href: "/", label: "Dashboard", icon: LayoutDashboard },
			{ href: "/operations", label: "Operations", icon: Activity },
		],
	},
	{
		// Chromium is the main release stream. Profiles also gets its own
		// entry — operators jump there directly often enough that hiding it
		// inside Chromium > major > tab adds friction. The Chromium drill-in
		// still exposes a Profiles tab scoped to one major; this sidebar item
		// is the catalog-wide view. Worker (local-api) images update via
		// Infrastructure > Updates, not a release stream here.
		label: "Releases",
		items: [
			{ href: "/releases", label: "Overview", icon: Package, exact: true },
			{ href: "/releases/chromium", label: "Chromium", icon: Globe },
			{ href: "/releases/profiles", label: "Profiles", icon: Boxes },
			{ href: "/manifest", label: "Manifest", icon: GitBranch },
			{ href: "/uploads", label: "Uploads", icon: Upload },
		],
	},
	{
		label: "Infrastructure",
		items: [
			// Workers first: it is the inventory, and the load balancer is a
			// property of the workers in it rather than a separate list of them.
			{ href: "/workers", label: "Workers", icon: Server },
			{
				href: "/cluster",
				label: "Load Balancer",
				icon: Network,
				indicator: true,
				items: [
					{ href: "/cluster/routing", label: "Routing" },
					{ href: "/cluster/security", label: "Security" },
				],
			},
			{ href: "/sessions", label: "Live sessions", icon: MonitorPlay },
			{ href: "/updates", label: "Updates", icon: CircleArrowUp },
		],
	},
	{
		label: "Admin",
		items: [{ href: "/settings", label: "Settings", icon: SettingsIcon }],
	},
];

/**
 * The path the shell reflects while the catch-all route is on screen. It
 * matches no nav item, so nothing in the sidebar stays lit for an address that
 * resolved to nothing, and its label below keeps the breadcrumb to
 * "Dashboard / Not found" instead of a trail built from segments that never
 * named a page.
 */
export const NOT_FOUND_PATH = "/not-found";

export const ROUTE_LABELS: Record<string, string> = {
	"": "Dashboard",
	"not-found": "Not found",
	operations: "Operations",
	releases: "Releases",
	manifest: "Manifest",
	uploads: "Uploads",
	cluster: "Load Balancer",
	routing: "Routing",
	security: "Security",
	workers: "Workers",
	// Without this the breadcrumb rendered the raw `history` segment.
	history: "History",
	sessions: "Live sessions",
	updates: "Updates",
	settings: "Settings",
	new: "New",
	chromium: "Chromium",
	profiles: "Profiles",
};

export interface NavDestination {
	href: string;
	/** "Load Balancer / Routing" for nested entries, plain label otherwise. */
	label: string;
	section: string;
	icon?: NavSection["items"][number]["icon"];
}

/**
 * Flatten the sidebar (parents and their sub-items) into a searchable list.
 * `hidden` is honoured so role-gated destinations never show up in search.
 */
export function flattenNav(sections: NavSection[]): NavDestination[] {
	const out: NavDestination[] = [];
	for (const section of sections) {
		const sectionLabel = section.label ?? "Navigation";
		for (const item of section.items) {
			if (item.hidden) continue;
			out.push({
				href: item.href,
				label: item.label,
				section: sectionLabel,
				icon: item.icon,
			});
			for (const sub of item.items ?? []) {
				if (sub.hidden) continue;
				out.push({
					href: sub.href,
					label: `${item.label} / ${sub.label}`,
					section: sectionLabel,
					icon: item.icon,
				});
			}
		}
	}
	return out;
}
